import { useCallback, useMemo, useState, useEffect } from 'react';

import { useHistory, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import isPast from 'date-fns/isPast';
import isFuture from 'date-fns/isFuture';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { ptBR } from 'date-fns/locale';
import {
  FiChevronLeft,
  FiClock,
  FiMonitor,
  FiUsers,
  FiPlay,
  FiCalendar,
} from 'react-icons/fi';
import ReactSelect from 'react-select';

import { MetaTags } from '@components';
import {
  useSelectRoomsQuery,
  useSpeakersQuery,
  useScheduleQuery,
} from '@hooks';
import { generateArray } from '@utils';
import {
  FullSpeakerData,
  FullScheduleData,
  SelectRoomsData,
} from '@common/types/api';

import SchedulePanel from '../Lobby/LobbySidebar/SchedulePanel';
import {
  LiveHeroWrapper,
  LiveBadge as RoomLiveBadge,
  LiveBadgeDot,
  LiveGlassCard,
  LiveRoomName,
  LiveNowPlaying,
  LiveNowPlayingHeader,
  LiveNowLabel,
  EqualizerBars,
  EqualizerBar,
  LiveSessionTitle,
  LiveSpeakerRow,
  LiveSpeakerAvatar,
  LiveSpeakerFallback,
  LiveSpeakerNames,
  LiveProgressWrapper,
  LiveProgressTrack,
  LiveProgressFill,
  LiveProgressTime,
  LiveEnterButton,
  IdleHeroWrapper,
  IdleBadge,
  IdleDot,
  NextUpCard as RoomNextUpCard,
  NextUpLabel,
  NextUpRoomName,
  NextUpSessionTitle as RoomNextUpSessionTitle,
  NextUpDateTime,
  NextUpCountdown,
} from '../Rooms/styles';
import * as S from './styles';

const isSessionLive = (session: FullScheduleData) => {
  if (!session.schedule_date || !session.start_time || !session.end_time) {
    return false;
  }
  const start = new Date(`${session.schedule_date}T${session.start_time}`);
  const end = new Date(`${session.schedule_date}T${session.end_time}`);
  return isPast(start) && isFuture(end);
};

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const findNextSession = (
  schedule: FullScheduleData[],
  liveSession: FullScheduleData | undefined,
) => {
  if (liveSession) return undefined;
  const now = new Date();
  return schedule.find((s) => {
    if (!s.schedule_date || !s.start_time) return false;
    const start = new Date(`${s.schedule_date}T${s.start_time}`);
    return start > now;
  });
};

// ── Progress & Equalizer helpers ────────────────────────

const getSessionProgress = (session: FullScheduleData): number => {
  if (!session.schedule_date || !session.start_time || !session.end_time)
    return 0;
  const start = new Date(`${session.schedule_date}T${session.start_time}`);
  const end = new Date(`${session.schedule_date}T${session.end_time}`);
  const now = new Date();
  const total = end.getTime() - start.getTime();
  if (total <= 0) return 0;
  return ((now.getTime() - start.getTime()) / total) * 100;
};

const EQ_DELAYS = [0, 0.2, 0.4, 0.15];

const formatTime = (t: string) => (t ? t.slice(0, 5) : '');

// ── Sub-components ──────────────────────────────────────

type SpeakerCardItemProps = {
  speaker: FullSpeakerData;
  onClick: () => void;
};

const SpeakerCardItem = ({ speaker, onClick }: SpeakerCardItemProps) => (
  <S.SpeakerCard onClick={onClick}>
    <S.CardPhotoArea>
      {speaker.photo ? (
        <S.CardAvatar src={speaker.photo} alt={speaker.name} />
      ) : (
        <S.CardAvatarFallback>{getInitials(speaker.name)}</S.CardAvatarFallback>
      )}
    </S.CardPhotoArea>

    <S.CardInfo>
      <S.CardName>{speaker.name}</S.CardName>
      <S.CardTitle>{speaker.title}</S.CardTitle>
      {speaker.company_logo && (
        <S.CompanyLogo>
          <img src={speaker.company_logo} alt="Company" />
        </S.CompanyLogo>
      )}
    </S.CardInfo>
  </S.SpeakerCard>
);

const SpeakerSidebarLive = ({ session }: { session: FullScheduleData }) => {
  const roomSlug = session.room?.data?.slug;
  const roomName = session.room?.data?.name;
  const speakers = session.participants?.data?.slice(0, 3) ?? [];
  const names = speakers.map((s) => s.name.split(' ')[0]).join(', ');
  const [progress, setProgress] = useState(getSessionProgress(session));

  useEffect(() => {
    setProgress(getSessionProgress(session));
    const timer = setInterval(() => {
      setProgress(getSessionProgress(session));
    }, 10000);
    return () => clearInterval(timer);
  }, [session]);

  return (
    <LiveHeroWrapper style={{ margin: '0 0 1rem' }}>
      <RoomLiveBadge>
        <LiveBadgeDot />
        AO VIVO
      </RoomLiveBadge>

      <LiveGlassCard>
        {roomName && (
          <LiveRoomName>
            <FiMonitor size={14} />
            {roomName}
          </LiveRoomName>
        )}

        <LiveNowPlaying>
          <LiveNowPlayingHeader>
            <EqualizerBars>
              {EQ_DELAYS.map((d) => (
                <EqualizerBar key={`eq-${d}`} $delay={d} />
              ))}
            </EqualizerBars>
            <LiveNowLabel>ACONTECENDO AGORA</LiveNowLabel>
          </LiveNowPlayingHeader>
          <LiveSessionTitle>{session.title}</LiveSessionTitle>
        </LiveNowPlaying>

        {speakers.length > 0 && (
          <LiveSpeakerRow>
            {speakers.map((sp, i) =>
              sp.photo ? (
                <LiveSpeakerAvatar
                  key={sp.id}
                  src={sp.photo}
                  alt={sp.name}
                  $index={i}
                />
              ) : (
                <LiveSpeakerFallback key={sp.id} $index={i}>
                  {getInitials(sp.name)}
                </LiveSpeakerFallback>
              ),
            )}
            <LiveSpeakerNames>{names}</LiveSpeakerNames>
          </LiveSpeakerRow>
        )}

        <LiveProgressWrapper>
          <LiveProgressTime>{formatTime(session.start_time)}</LiveProgressTime>
          <LiveProgressTrack>
            <LiveProgressFill $percent={progress} />
          </LiveProgressTrack>
          <LiveProgressTime>{formatTime(session.end_time)}</LiveProgressTime>
        </LiveProgressWrapper>
      </LiveGlassCard>

      <LiveEnterButton to={roomSlug ? `/broadcast/${roomSlug}` : '#'}>
        <FiPlay size={14} />
        Entrar na sala
      </LiveEnterButton>
    </LiveHeroWrapper>
  );
};

const SpeakerSidebarNoLive = ({ session }: { session: FullScheduleData }) => {
  const startDate = session.schedule_date
    ? parseISO(`${session.schedule_date}T${session.start_time || '00:00'}`)
    : new Date();
  const countdown = formatDistanceToNow(startDate, {
    locale: ptBR,
    addSuffix: true,
  });

  return (
    <IdleHeroWrapper style={{ margin: '0 0 1rem' }}>
      <IdleBadge>
        <IdleDot />
        Nenhuma transmissão ao vivo
      </IdleBadge>

      <RoomNextUpCard>
        <NextUpLabel>
          <FiClock size={12} />
          Próxima transmissão
        </NextUpLabel>

        {session.room?.data?.name && (
          <NextUpRoomName>{session.room.data.name}</NextUpRoomName>
        )}

        <RoomNextUpSessionTitle>{session.title}</RoomNextUpSessionTitle>

        <NextUpDateTime>
          <span>
            <FiCalendar size={12} />
            {session.schedule_date
              ? format(parseISO(session.schedule_date), 'dd MMM', {
                  locale: ptBR,
                })
              : ''}
          </span>
          <span>
            <FiClock size={12} />
            {formatTime(session.start_time)}
          </span>
          <NextUpCountdown>· {countdown}</NextUpCountdown>
        </NextUpDateTime>
      </RoomNextUpCard>
    </IdleHeroWrapper>
  );
};

type SpeakerGridContentProps = {
  isLoading: boolean;
  speakers: FullSpeakerData[];
  allCount: number;
  onSpeakerClick: (id: number) => void;
  onClearFilters: () => void;
  emptyTitle: string;
  emptySubtitle: string;
  clearLabel: string;
};

const SpeakerGridContent = ({
  isLoading,
  speakers,
  allCount,
  onSpeakerClick,
  onClearFilters,
  emptyTitle,
  emptySubtitle,
  clearLabel,
}: SpeakerGridContentProps) => {
  if (isLoading) {
    return (
      <>
        {generateArray(6).map((key) => (
          <S.SkeletonCard key={key}>
            <S.SkeletonPhotoArea>
              <Skeleton height="100%" />
            </S.SkeletonPhotoArea>
            <S.SkeletonInfo>
              <Skeleton width="70%" />
              <Skeleton width="50%" />
              <Skeleton height="2.5rem" style={{ marginTop: '0.5rem' }} />
            </S.SkeletonInfo>
          </S.SkeletonCard>
        ))}
      </>
    );
  }

  if (speakers.length === 0 && allCount === 0) {
    return (
      <S.EmptyState>
        <FiUsers size={44} />
        <h3>Nenhum palestrante ainda</h3>
        <p>Os palestrantes aparecerão aqui assim que forem cadastrados.</p>
      </S.EmptyState>
    );
  }

  if (speakers.length === 0 && allCount > 0) {
    return (
      <S.EmptyState>
        <FiUsers size={44} />
        <h3>{emptyTitle}</h3>
        <p>{emptySubtitle}</p>
        <S.ClearFiltersButton onClick={onClearFilters}>
          {clearLabel}
        </S.ClearFiltersButton>
      </S.EmptyState>
    );
  }

  return (
    <>
      {speakers.map((speaker) => (
        <SpeakerCardItem
          key={`speaker-${speaker.id}`}
          speaker={speaker}
          onClick={() => onSpeakerClick(speaker.id)}
        />
      ))}
    </>
  );
};

type RoomFilterDropdownProps = {
  rooms: SelectRoomsData[];
  activeRoom: number | null;
  onRoomChange: (roomId: number | null) => void;
};

const RoomFilterDropdown = ({
  rooms,
  activeRoom,
  onRoomChange,
}: RoomFilterDropdownProps) => {
  const options = [
    { value: '', label: 'Todas' },
    ...rooms.map((r) => ({ value: String(r.value), label: r.label })),
  ];

  const activeOption =
    options.find((o) => o.value === String(activeRoom || '')) || options[0];

  const handleChange = (opt: { value: string; label: string } | null) => {
    if (!opt || !opt.value) {
      onRoomChange(null);
      return;
    }
    onRoomChange(Number(opt.value));
  };

  return (
    <S.RoomDropdown>
      <ReactSelect
        value={activeOption}
        onChange={handleChange}
        options={options}
        isSearchable={false}
        menuPlacement="auto"
        className="room-select-container"
        classNamePrefix="room-select"
        components={{
          SingleValue: ({
            data,
          }: {
            data: { value: string; label: string };
          }) => (
            <div className="room-select__custom-value">
              <FiMonitor size={14} className="room-value-icon" />
              <span className="room-value-label">Sala:</span>
              <strong className="room-value-name">
                {data.value === '' ? 'Todas' : data.label}
              </strong>
            </div>
          ),
        }}
        formatOptionLabel={(opt) => (
          <span>{opt.value === '' ? 'Todas as salas' : opt.label}</span>
        )}
      />
    </S.RoomDropdown>
  );
};

// ── Main Component ──────────────────────────────────────

const Speakers = () => {
  const history = useHistory();
  const { search, ...location } = useLocation();
  const { t } = useTranslation();
  const [activeRoom, setActiveRoom] = useState<number | null>(null);

  const selectRoomsQuery = useSelectRoomsQuery(true);
  const speakersQuery = useSpeakersQuery(1);
  const scheduleQuery = useScheduleQuery(1, {});

  const allSpeakers = useMemo(
    () => speakersQuery.data?.speakers ?? [],
    [speakersQuery.data?.speakers],
  );

  const allSchedule = useMemo(
    () => scheduleQuery.data?.schedule ?? [],
    [scheduleQuery.data?.schedule],
  );

  const filteredSpeakers = useMemo(
    () =>
      allSpeakers.filter(
        (speaker) =>
          !activeRoom ||
          speaker.rooms.data.some((room) => room.id === activeRoom),
      ),
    [allSpeakers, activeRoom],
  );

  const liveSession = useMemo(
    () => allSchedule.find(isSessionLive),
    [allSchedule],
  );

  const nextSession = useMemo(
    () => findNextSession(allSchedule, liveSession),
    [allSchedule, liveSession],
  );

  const handleSpeaker = useCallback(
    (speakerId: number) => {
      const queryParams = new URLSearchParams(search);
      queryParams.set('speaker', `${speakerId}`);

      history.replace({
        ...location,
        search: `?${queryParams.toString()}`,
      });
    },
    [search, location, history],
  );

  const handleClearFilters = () => {
    setActiveRoom(null);
  };

  return (
    <S.PageWrapper>
      <MetaTags title={t('pages.speakers.title')} />

      <S.MainContent>
        <S.PageHeader>
          <S.PageHeaderLeft>
            <S.BackButton type="button" onClick={() => history.goBack()}>
              <FiChevronLeft size="1.375rem" />
            </S.BackButton>
            <h1>{t('pages.speakers.title')}</h1>
            {/* {!speakersQuery.isLoading && (
              <S.CountBadge>{allSpeakers.length} confirmados</S.CountBadge>
            )} */}
          </S.PageHeaderLeft>
        </S.PageHeader>

        {selectRoomsQuery.data && selectRoomsQuery.data.length > 0 && (
          <S.SearchFiltersRow>
            <RoomFilterDropdown
              rooms={selectRoomsQuery.data}
              activeRoom={activeRoom}
              onRoomChange={setActiveRoom}
            />
          </S.SearchFiltersRow>
        )}

        <S.SpeakerGrid>
          <SpeakerGridContent
            isLoading={speakersQuery.isLoading}
            speakers={filteredSpeakers}
            allCount={allSpeakers.length}
            onSpeakerClick={handleSpeaker}
            onClearFilters={handleClearFilters}
            emptyTitle={t(
              'pages.speakers.empty',
              'Nenhum palestrante encontrado',
            )}
            emptySubtitle={t(
              'pages.speakers.emptyHint',
              'Tente ajustar os filtros.',
            )}
            clearLabel={t('pages.speakers.clearFilters', 'Limpar filtros')}
          />
        </S.SpeakerGrid>
      </S.MainContent>

      <S.Sidebar>
        {liveSession ? (
          <SpeakerSidebarLive session={liveSession} />
        ) : (
          nextSession && <SpeakerSidebarNoLive session={nextSession} />
        )}

        <S.ScheduleWrapper>
          <SchedulePanel scheduleQuery={scheduleQuery} />
          <S.ScheduleFade />
        </S.ScheduleWrapper>
      </S.Sidebar>
    </S.PageWrapper>
  );
};

export default Speakers;
